import { Link } from 'react-router-dom';
import DashboardLayout from '@/components/feature/DashboardLayout';
import { useAuth } from '@/hooks/useAuth';
import { StatCard, AlertCard, PageHeader, KpiBreakdownCard } from '@/pages/dashboard/shared';
import { usePainel, type PainelAlerta, type PainelAtividade } from '@/pages/dashboard/shared-data';

// Ícone por tipo de alerta. O tipo vem do banco; a apresentação fica aqui.
const ICONE_DO_ALERTA: Record<PainelAlerta['tipo'], string> = {
  alvara: 'ri-file-warning-line',
  manutencao: 'ri-tools-line',
  treinamento: 'ri-award-line',
  reuniao: 'ri-group-line',
};

/** "Hoje, 10:30", "Ontem, 16:45" ou a data — como se lê um feed. */
function quandoLegivel(iso: string): string {
  const data = new Date(iso);
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  const dia = new Date(data);
  dia.setHours(0, 0, 0, 0);

  const diferenca = Math.round((hoje.getTime() - dia.getTime()) / 86_400_000);
  const hora = data.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

  if (diferenca === 0) return `Hoje, ${hora}`;
  if (diferenca === 1) return `Ontem, ${hora}`;
  return data.toLocaleDateString('pt-BR');
}

function CartaoVazio({ mensagem }: { mensagem: string }) {
  return <p className="text-sm text-foreground-500 py-6 text-center">{mensagem}</p>;
}

export default function AlertasKpisPage() {
  const { effectiveAccountId } = useAuth();
  const { painel, loading, erro } = usePainel(effectiveAccountId);

  const treinamentoSegments = [
    { label: 'Vigentes', count: painel?.treinamentos.vigentes ?? 0, color: 'ok' as const },
    { label: 'A vencer', count: painel?.treinamentos.a_vencer ?? 0, color: 'warning' as const },
    { label: 'Vencidos', count: painel?.treinamentos.vencidos ?? 0, color: 'expired' as const },
  ];

  const alvarasSegments = [
    { label: 'Vigentes', count: painel?.alvaras.vigentes ?? 0, color: 'ok' as const },
    { label: 'Pendentes', count: painel?.alvaras.pendentes ?? 0, color: 'warning' as const },
    { label: 'Vencidos', count: painel?.alvaras.vencidos ?? 0, color: 'expired' as const },
  ];

  // Enquanto carrega, o número fica em branco em vez de piscar um zero: zero é
  // um dado, e mostrá-lo antes da resposta faz o painel mentir por um instante.
  const numero = (valor: number | undefined, formatar?: (v: number) => string) => {
    if (loading || valor === undefined) return '—';
    return formatar ? formatar(valor) : String(valor);
  };

  const alertas: PainelAlerta[] = painel?.alertas ?? [];
  const atividade: PainelAtividade[] = painel?.atividade ?? [];

  return (
    <DashboardLayout>
      <div className="max-w-6xl">
        <PageHeader
          title="Alertas e KPIs"
          subtitle="Visão geral da operação dos seus condomínios"
        />

        {erro && (
          <div className="mb-6 px-4 py-3 rounded-lg border border-red-200 bg-red-50 text-sm text-red-700">
            Não foi possível carregar os indicadores: {erro}
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
          <StatCard
            label="Condomínios ativos"
            value={numero(painel?.condominios_ativos)}
            icon="ri-building-line"
            color="primary"
          />
          <StatCard
            label="Imóveis cadastrados"
            value={numero(painel?.imoveis, (v) => v.toLocaleString('pt-BR'))}
            icon="ri-home-4-line"
            color="primary"
          />
          <StatCard
            label="Saúde geral"
            value={numero(painel?.saude, (v) => `${v}%`)}
            icon="ri-heart-pulse-line"
            color="accent"
          />
          <StatCard
            label="Pendências abertas"
            value={numero(painel?.pendencias_abertas)}
            icon="ri-error-warning-line"
            color="accent"
          />
          <StatCard
            label="Manutenções no mês"
            value={numero(painel?.manutencoes_mes)}
            icon="ri-tools-line"
            color="secondary"
          />
          <KpiBreakdownCard
            label="Treinamentos"
            icon="ri-award-line"
            segments={treinamentoSegments}
          />
          <KpiBreakdownCard
            label="Alvarás"
            icon="ri-file-shield-2-line"
            segments={alvarasSegments}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Alertas recentes */}
          <div className="bg-background-50 border border-background-200/70 rounded-lg">
            <div className="px-5 py-4 border-b border-background-200/70 flex items-center justify-between">
              <h2 className="text-base font-semibold text-foreground-900">Alertas recentes</h2>
              <Link
                to="/saude-pendencias"
                className="text-sm text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap"
              >
                Ver todos
              </Link>
            </div>
            <div className="p-4 flex flex-col gap-3">
              {loading && <CartaoVazio mensagem="Carregando alertas..." />}
              {!loading && alertas.length === 0 && (
                <CartaoVazio mensagem="Nenhum vencimento nos próximos 30 dias." />
              )}
              {alertas.map((alerta) => (
                <AlertCard
                  key={alerta.id}
                  type={alerta.prioridade === 'alta' ? 'warning' : 'info'}
                  message={alerta.titulo}
                  condo={alerta.condominio}
                  icon={ICONE_DO_ALERTA[alerta.tipo] || 'ri-error-warning-line'}
                />
              ))}
            </div>
          </div>

          {/* Atividade recente */}
          <div className="bg-background-50 border border-background-200/70 rounded-lg">
            <div className="px-5 py-4 border-b border-background-200/70">
              <h2 className="text-base font-semibold text-foreground-900">Atividade recente</h2>
            </div>
            <div className="p-4">
              {loading && <CartaoVazio mensagem="Carregando atividade..." />}
              {!loading && atividade.length === 0 && (
                <CartaoVazio mensagem="Nenhum cadastro recente." />
              )}
              <ul className="flex flex-col">
                {atividade.map((item, idx) => (
                  <li
                    key={item.id}
                    className={`flex items-start gap-3 py-3 ${
                      idx !== atividade.length - 1 ? 'border-b border-background-200/50' : ''
                    }`}
                  >
                    <div className="w-8 h-8 rounded-full bg-background-100 flex items-center justify-center shrink-0 mt-0.5">
                      <i className="ri-history-line text-foreground-500 text-sm"></i>
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm text-foreground-800">{item.acao}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-xs text-foreground-500">{item.condominio}</span>
                        <span className="text-xs text-foreground-400">•</span>
                        <span className="text-xs text-foreground-500">
                          {quandoLegivel(item.quando)}
                        </span>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
